import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Loader2,
  XCircle,
  ArrowLeft,
  Clock,
  Calendar,
  Radio,
  Download,
  Copy,
  Check,
  Search,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  ShieldAlert,
  Lightbulb,
  FileText,
  ExternalLink,
  Play,
  PanelRight,
  PanelRightClose,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScoreGauge } from '@/components/ScoreGauge';
import { useJobResults } from '@/hooks/useJob';
import type { Recommendation, ClaimToVerify, ClaimVerdict, RiskFlag, TopSegment, TranscriptSegment } from '@/types';
import { cn } from '@/lib/utils';

// --- Helpers ---

const SPEAKER_COLORS = [
  { text: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-200', dot: 'bg-blue-500' },
  { text: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  { text: 'text-purple-700', bg: 'bg-purple-100', border: 'border-purple-200', dot: 'bg-purple-500' },
  { text: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-200', dot: 'bg-orange-500' },
  { text: 'text-pink-700', bg: 'bg-pink-100', border: 'border-pink-200', dot: 'bg-pink-500' },
  { text: 'text-teal-700', bg: 'bg-teal-100', border: 'border-teal-200', dot: 'bg-teal-500' },
];

function getSpeakerColor(speaker: string, speakerMap: Map<string, number>) {
  if (!speakerMap.has(speaker)) {
    speakerMap.set(speaker, speakerMap.size);
  }
  return SPEAKER_COLORS[speakerMap.get(speaker)! % SPEAKER_COLORS.length];
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function extractYouTubeId(url: string): string | null {
  try {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

function getDirectMediaType(url: string): 'audio' | 'video' | null {
  try {
    const clean = url.split('?')[0].toLowerCase();
    if (/\.(mp3|m4a|aac|ogg|opus|wav|flac)$/.test(clean)) return 'audio';
    if (/\.(mp4|webm|mov|avi|mkv)$/.test(clean)) return 'video';
    return null;
  } catch {
    return null;
  }
}

// Risk markers that belong in the Risk Flags tab, not the segment headline row
const RISK_FLAG_MARKERS = new Set([
  'bias',
  'biased',
  'sensitivity',
  'sensitive',
  'misinformation',
  'disinformation',
  'misleading',
  'unverified',
]);

function isRiskFlagMarker(marker: string): boolean {
  return RISK_FLAG_MARKERS.has(marker.toLowerCase());
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ============================================================
// YouTube Player Component — minimal inline types for YT IFrame API
// ============================================================

interface YTPlayer {
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  playVideo(): void;
  getCurrentTime(): number;
  destroy(): void;
}

interface YTPlayerEvent {
  target: YTPlayer;
  data?: number;
}

declare global {
  interface Window {
    YT: {
      Player: new (
        el: HTMLElement,
        opts: {
          videoId: string;
          playerVars?: Record<string, number>;
          events?: { onReady?: (e: YTPlayerEvent) => void };
        },
      ) => YTPlayer;
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YouTubePlayerProps {
  videoId: string;
  onPlayerReady: (player: YTPlayer) => void;
}

function YouTubePlayer({ videoId, onPlayerReady }: YouTubePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);

  useEffect(() => {
    const initPlayer = () => {
      if (!containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: { autoplay: 0, modestbranding: 1, rel: 0 },
        events: {
          onReady: (e: YTPlayerEvent) => onPlayerReady(e.target),
        },
      });
    };

    if (window.YT?.Player) {
      initPlayer();
    } else {
      // Load YouTube IFrame API if not already loaded
      if (!document.getElementById('yt-iframe-api')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      playerRef.current?.destroy();
    };
  }, [videoId]);

  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden bg-black">
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}

// ============================================================
// Native Audio / Video players (for non-YouTube direct URLs)
// ============================================================

interface NativeMediaPlayerProps {
  url: string;
  type: 'audio' | 'video';
  mediaRef: React.RefObject<HTMLAudioElement | HTMLVideoElement | null>;
}

function NativeMediaPlayer({ url, type, mediaRef }: NativeMediaPlayerProps) {
  if (type === 'audio') {
    return (
      <audio
        ref={mediaRef as React.RefObject<HTMLAudioElement>}
        src={url}
        controls
        className="w-full rounded-lg"
        preload="metadata"
      />
    );
  }
  return (
    <video
      ref={mediaRef as React.RefObject<HTMLVideoElement>}
      src={url}
      controls
      className="w-full aspect-video rounded-lg bg-black"
      preload="metadata"
    />
  );
}

// ============================================================
// Seek button — plays the video at a given timestamp
// ============================================================

function SeekButton({
  seconds,
  onSeek,
  className,
}: {
  seconds: number;
  onSeek: ((seconds: number) => void) | null;
  className?: string;
}) {
  if (!onSeek) return null;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onSeek(seconds);
      }}
      className={cn('inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline', className)}
      title={`Play at ${formatTimestamp(seconds)}`}
    >
      <Play className="h-3 w-3 fill-current" />
      {formatTimestamp(seconds)}
    </button>
  );
}

// ============================================================
// Expandable top segment
// ============================================================

const RECOMMENDATION_CONFIG: Record<Recommendation, { label: string; className: string }> = {
  ignore: {
    label: 'Ignore',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  },
  monitor: {
    label: 'Monitor',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  review_now: {
    label: 'Review Now',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  urgent_review: {
    label: 'Urgent Review',
    className: 'bg-red-100 text-red-800 border-red-200',
  },
};

function PriorityBadge({ priority }: { priority: ClaimToVerify['priority'] }) {
  const styles = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-slate-100 text-slate-700',
  };
  return (
    <Badge className={cn('capitalize', styles[priority])} variant="outline">
      {priority}
    </Badge>
  );
}

function VerdictBadge({ verdict }: { verdict: ClaimVerdict }) {
  const styles: Record<ClaimVerdict, { label: string; className: string }> = {
    true: { label: 'Verified True', className: 'bg-green-100 text-green-800 border-green-200' },
    false: { label: 'False', className: 'bg-red-100 text-red-800 border-red-200' },
    misleading: { label: 'Misleading', className: 'bg-orange-100 text-orange-800 border-orange-200' },
    partially_true: { label: 'Partially True', className: 'bg-amber-100 text-amber-800 border-amber-200' },
    unverifiable: { label: 'Unverifiable', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  };
  const s = styles[verdict] ?? styles.unverifiable;
  return (
    <Badge className={cn(s.className)} variant="outline">
      {s.label}
    </Badge>
  );
}

function SeverityBadge({ severity }: { severity: RiskFlag['severity'] }) {
  const styles = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-amber-100 text-amber-800 border-amber-200',
    low: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <Badge className={cn('capitalize', styles[severity])} variant="outline">
      {severity}
    </Badge>
  );
}

function ExpandableSegment({ seg, onSeek }: { seg: TopSegment; onSeek: ((seconds: number) => void) | null }) {
  const [expanded, setExpanded] = useState(false);

  // Only show markers that are NOT risk-flag-type (bias, sensitivity, misinformation)
  const displayMarkers = seg.riskMarkers.filter((m) => !isRiskFlagMarker(m));

  return (
    <div className={cn('rounded-lg border p-4 transition-colors hover:bg-accent/30', expanded && 'bg-accent/20')}>
      <div className="flex items-start gap-4">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold mt-0.5">
          {seg.rank}
        </span>
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Header row: timestamp, score, non-risk markers, seek button, expand toggle */}
          <div className="flex items-center gap-3 flex-wrap cursor-pointer" onClick={() => setExpanded(!expanded)}>
            <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap">
              {formatTimestamp(seg.startTime)} – {formatTimestamp(seg.endTime)}
            </span>
            <span className="text-sm font-semibold tabular-nums">{seg.score}</span>
            {displayMarkers.map((marker, i) => (
              <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                {marker}
              </Badge>
            ))}
            <span className="ml-auto flex items-center gap-2 shrink-0">
              <SeekButton seconds={seg.startTime} onSeek={onSeek} />
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </span>
          </div>

          {/* Snippet */}
          <p className={cn('text-sm cursor-pointer', !expanded && 'line-clamp-2')} onClick={() => setExpanded(!expanded)}>
            {seg.snippet}
          </p>

          {/* Expanded: reason + transcript context */}
          {expanded && (
            <div className="space-y-2 pt-1">
              <p className="text-xs text-muted-foreground">{seg.reason}</p>
              {seg.riskMarkers.filter(isRiskFlagMarker).length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  <span className="font-medium">Risk flags: </span>
                  {seg.riskMarkers.filter(isRiskFlagMarker).join(', ')} — see the <span className="font-medium">Risk Flags</span>{' '}
                  tab for details.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Sentence splitter — expands one long segment into sub-sentences
// with proportionally distributed timestamps
// ============================================================

interface FlatSegment {
  startTime: number;
  endTime: number;
  speaker: string | null;
  text: string;
  /** true when this is a continuation sentence from the same original segment */
  isContinuation: boolean;
}

function splitSegmentsBySentence(segments: TranscriptSegment[]): FlatSegment[] {
  const result: FlatSegment[] = [];

  for (const seg of segments) {
    const duration = seg.endTime - seg.startTime;
    // Split on sentence boundaries — period/exclamation/question followed by space or end-of-string
    // Keep the punctuation attached to the preceding sentence
    const sentences = seg.text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (sentences.length <= 1) {
      result.push({
        startTime: seg.startTime,
        endTime: seg.endTime,
        speaker: seg.speaker ?? null,
        text: seg.text,
        isContinuation: false,
      });
      continue;
    }

    // Distribute time proportionally by character count
    const totalChars = sentences.reduce((sum, s) => sum + s.length, 0);
    let offset = seg.startTime;

    sentences.forEach((sentence, i) => {
      const ratio = sentence.length / totalChars;
      const subDuration = duration * ratio;
      const subStart = offset;
      const subEnd = i === sentences.length - 1 ? seg.endTime : offset + subDuration;
      result.push({
        startTime: subStart,
        endTime: subEnd,
        speaker: seg.speaker ?? null,
        text: sentence,
        isContinuation: i > 0,
      });
      offset = subEnd;
    });
  }

  return result;
}

// ============================================================
// Transcript Panel — collapsible right panel with live sync
// ============================================================

interface TranscriptPanelProps {
  transcript: TranscriptSegment[];
  speakerColorMap: Map<string, number>;
  highlightedTimestamps: Set<number>;
  currentTime: number;
  hasPlayer: boolean;
  onSeek: (seconds: number) => void;
  onClose: () => void;
}

function TranscriptPanel({
  transcript,
  speakerColorMap,
  highlightedTimestamps,
  currentTime,
  onSeek,
  onClose,
}: TranscriptPanelProps) {
  const [search, setSearch] = useState('');
  const [userScrolled, setUserScrolled] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const userScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expand all segments into sentences for finer-grained highlighting
  const splitSegments = useMemo(() => splitSegmentsBySentence(transcript), [transcript]);

  const filtered = useMemo(() => {
    if (!search.trim()) return splitSegments;
    const q = search.toLowerCase();
    return splitSegments.filter((seg) => seg.text.toLowerCase().includes(q));
  }, [splitSegments, search]);

  // Active segment = the last one whose startTime ≤ currentTime
  const activeIdx = useMemo(() => {
    if (currentTime <= 0) return -1;
    for (let i = filtered.length - 1; i >= 0; i--) {
      if (filtered[i].startTime <= currentTime) return i;
    }
    return -1;
  }, [filtered, currentTime]);

  // Auto-scroll to active segment (unless user is manually scrolling)
  // block: 'start' ensures the active row sits at the TOP of the panel — important for long segments
  useEffect(() => {
    if (userScrolled || activeIdx === -1 || !activeRef.current || !scrollRef.current) return;
    activeRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [activeIdx, userScrolled]);

  // Detect manual scroll — pause auto-scroll for 4s
  const handleScroll = useCallback(() => {
    setUserScrolled(true);
    if (userScrollTimerRef.current) clearTimeout(userScrollTimerRef.current);
    userScrollTimerRef.current = setTimeout(() => setUserScrolled(false), 4000);
  }, []);

  const isHighlighted = (seg: FlatSegment): boolean => {
    if (highlightedTimestamps.size === 0) return false;
    for (const ts of highlightedTimestamps) {
      if (ts >= seg.startTime && ts <= seg.endTime) return true;
    }
    return false;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">Transcript</span>
          <span className="text-xs text-muted-foreground">({transcript.length} segments)</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" title="Close transcript">
          <PanelRightClose className="h-4 w-4" />
        </button>
      </div>

      {/* Speaker legend */}
      {speakerColorMap.size > 1 && (
        <div className="flex flex-wrap gap-1.5 px-4 py-2 border-b shrink-0">
          {Array.from(speakerColorMap.entries()).map(([name, idx]) => {
            const c = SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
            return (
              <span
                key={name}
                className={cn('inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5', c.bg, c.text)}
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', c.dot)} />
                {name}
              </span>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="px-3 py-2 border-b shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      {/* Scrollable segment list */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No matching segments.</p>
        ) : (
          filtered.map((seg, i) => {
            const isActive = i === activeIdx;
            const highlighted = isHighlighted(seg);
            const speakerColor = seg.speaker ? getSpeakerColor(seg.speaker, speakerColorMap) : null;

            return (
              <div
                key={i}
                ref={isActive ? activeRef : null}
                onClick={() => onSeek(seg.startTime)}
                className={cn(
                  'flex gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors',
                  isActive
                    ? 'bg-primary/10 border-l-2 border-primary'
                    : highlighted
                      ? 'bg-amber-50 border-l-2 border-amber-400 hover:bg-amber-100/60'
                      : 'hover:bg-accent/50',
                )}
              >
                {/* Timestamp */}
                <span
                  className={cn(
                    'tabular-nums shrink-0 text-[11px] w-10 text-right mt-0.5',
                    isActive ? 'text-primary font-semibold' : 'text-muted-foreground',
                  )}
                >
                  {formatTimestamp(seg.startTime)}
                </span>

                <div className="flex-1 min-w-0 space-y-0.5">
                  {/* Speaker */}
                  {seg.speaker && speakerColor && (
                    <div className={cn('flex items-center gap-1 text-[10px] font-semibold', speakerColor.text)}>
                      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', speakerColor.dot)} />
                      {seg.speaker}
                    </div>
                  )}
                  {/* Text */}
                  <p className={cn('text-sm leading-snug', isActive && 'font-medium')}>{seg.text}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Resume auto-scroll button */}
      {userScrolled && activeIdx !== -1 && (
        <div className="px-3 py-2 border-t shrink-0">
          <button
            onClick={() => {
              setUserScrolled(false);
              activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }}
            className="w-full text-xs text-primary hover:underline text-center"
          >
            ↓ Resume auto-scroll
          </button>
        </div>
      )}
    </div>
  );
}

// --- Main component ---

export function ResultsPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { analysis, transcript, visualContext, job, loading, error } = useJobResults(jobId);
  const [copied, setCopied] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  // Determine media type
  const sourceUrl = job?.metadata?.sourceUrl ?? '';
  // The backend persists the EXACT audio it transcribed and returns a signed URL here. Prefer it
  // over sourceUrl for playback: re-downloading sourceUrl gives a differently ad-stitched (longer)
  // file due to the CDN's per-request dynamic ad insertion, which would desync every timestamp.
  const playbackUrl = job?.metadata?.playbackUrl ?? null;
  const videoId = useMemo(() => extractYouTubeId(sourceUrl), [sourceUrl]);
  // When we have a persisted playback URL it's always our transcoded OGG → treat as audio.
  // Otherwise fall back to sniffing the original source URL's extension.
  const directMediaType = useMemo(
    () => (videoId ? null : playbackUrl ? 'audio' : getDirectMediaType(sourceUrl)),
    [videoId, playbackUrl, sourceUrl],
  );
  // URL the native <audio>/<video> element actually loads — persisted audio wins.
  const mediaUrl = playbackUrl ?? sourceUrl;
  const hasPlayer = !!(videoId || directMediaType);

  // YouTube player — auto-open transcript when playback starts (state 1 = playing)
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const handlePlayerReady = useCallback((player: YTPlayer) => {
    ytPlayerRef.current = player;
    // Attach onStateChange after the player is ready by re-creating via postMessage listener
    // The IFrame API doesn't expose addEventListener after construction, so we patch via
    // a second YT.Player wrapper using the same iframe element — instead, we use a simpler
    // approach: poll for currentTime > 0 in the existing rAF loop and open then.
  }, []);

  // Native audio/video player
  const nativeMediaRef = useRef<HTMLAudioElement | HTMLVideoElement | null>(null);

  // Poll currentTime via rAF for smooth highlight sync
  useEffect(() => {
    let rafId: number;
    const tick = () => {
      let t = 0;
      if (ytPlayerRef.current) {
        try {
          t = ytPlayerRef.current.getCurrentTime() ?? 0;
        } catch {
          t = 0;
        }
      } else if (nativeMediaRef.current) {
        t = nativeMediaRef.current.currentTime ?? 0;
      }
      setCurrentTime(t);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Auto-open transcript panel when playback starts
  // For YouTube: currentTime goes > 0 as soon as the player plays
  // For native: also listen to the 'play' DOM event directly
  const hasTranscriptRef = useRef(false);
  useEffect(() => {
    hasTranscriptRef.current = !!(transcript && transcript.length > 0);
  }, [transcript]);

  const hasStarted = currentTime > 0;
  useEffect(() => {
    if (!hasTranscriptRef.current) return;
    if (hasStarted) {
      setTranscriptOpen(true);
    }
  }, [hasStarted]);

  // Native media: open immediately on 'play' event (before currentTime ticks)
  useEffect(() => {
    const el = nativeMediaRef.current;
    if (!el) return;
    const onPlay = () => {
      if (hasTranscriptRef.current) setTranscriptOpen(true);
    };
    el.addEventListener('play', onPlay);
    return () => el.removeEventListener('play', onPlay);
  });

  // Unified seekTo — works for YouTube, native audio, and native video
  const seekTo = useCallback((seconds: number) => {
    if (ytPlayerRef.current) {
      ytPlayerRef.current.seekTo(seconds, true);
      ytPlayerRef.current.playVideo();
    } else if (nativeMediaRef.current) {
      nativeMediaRef.current.currentTime = seconds;
      nativeMediaRef.current.play().catch(() => {});
    }
    document.getElementById('media-player-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Build stable speaker color map from transcript
  const speakerColorMap = useMemo(() => {
    const map = new Map<string, number>();
    if (transcript) {
      transcript.forEach((seg) => {
        if (seg.speaker && !map.has(seg.speaker)) {
          map.set(seg.speaker, map.size);
        }
      });
    }
    return map;
  }, [transcript]);

  // Highlighted timestamps from top segments, claims, risks
  const highlightedTimestamps = useMemo(() => {
    if (!analysis) return new Set<number>();
    const timestamps = new Set<number>();
    analysis.topSegments.forEach((s) => {
      timestamps.add(s.startTime);
      timestamps.add(s.endTime);
    });
    analysis.claimsToVerify.forEach((c) => timestamps.add(c.timestamp));
    analysis.riskFlags.forEach((r) => r.relatedTimestamps.forEach((t) => timestamps.add(t)));
    return timestamps;
  }, [analysis]);

  const handleDownloadJson = () => {
    const data = { job, analysis, transcript };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis-${jobId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = async () => {
    const data = { job, analysis, transcript };
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error state
  if (error || !analysis) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center gap-4">
              <XCircle className="h-12 w-12 text-destructive" />
              <div>
                <h2 className="text-lg font-semibold">Results unavailable</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {error || 'Analysis results not found. The job may still be processing.'}
                </p>
              </div>
              <div className="flex gap-2">
                {jobId && (
                  <Button asChild variant="outline">
                    <Link to={`/jobs/${jobId}`}>Check Status</Link>
                  </Button>
                )}
                <Button asChild variant="outline">
                  <Link to="/">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Home
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const metadata = job?.metadata;
  const rec = RECOMMENDATION_CONFIG[analysis.recommendation];
  const hasTranscript = !!(transcript && transcript.length > 0);

  return (
    // Outer shell — flex row so transcript panel sits beside main content
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* ── Main scrollable content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 pb-12 space-y-6">
          {/* Header Panel */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold tracking-tight truncate">{metadata?.episodeTitle || 'Untitled Episode'}</h1>
                  {metadata?.showTitle && <p className="text-muted-foreground mt-0.5">{metadata.showTitle}</p>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {metadata?.publishDate && (
                    <Badge variant="outline" className="gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(metadata.publishDate)}
                    </Badge>
                  )}
                  {metadata?.duration != null && metadata.duration > 0 && (
                    <Badge variant="outline" className="gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(metadata.duration)}
                    </Badge>
                  )}
                  {metadata?.sourcePlatform && (
                    <Badge variant="outline" className="gap-1">
                      <Radio className="h-3 w-3" />
                      {metadata.sourcePlatform}
                    </Badge>
                  )}
                  <Badge variant="secondary">{analysis.analysisScope} scope</Badge>
                  <Badge variant="secondary">{analysis.acquisitionMethod.replace(/_/g, ' ')}</Badge>
                  {/* Transcript toggle button */}
                  {hasTranscript && (
                    <Button
                      variant={transcriptOpen ? 'default' : 'outline'}
                      size="sm"
                      className="gap-1.5 h-7 px-2.5 text-xs"
                      onClick={() => setTranscriptOpen((o) => !o)}
                    >
                      {transcriptOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRight className="h-3.5 w-3.5" />}
                      Transcript
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Triage Panel — score gauge + video side by side, summary below */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              {/* Top row: gauge + player — responsive */}
              <div
                id="media-player-container"
                className={hasPlayer ? 'flex flex-col sm:flex-row items-center gap-6' : 'flex justify-center'}
              >
                <div className="shrink-0 flex justify-center">
                  <ScoreGauge score={analysis.newsworthinessScore} size={160} />
                </div>
                {videoId && (
                  <div className="w-full sm:flex-1 min-w-0">
                    <YouTubePlayer videoId={videoId} onPlayerReady={handlePlayerReady} />
                  </div>
                )}
                {!videoId && directMediaType && mediaUrl && (
                  <div className="w-full sm:flex-1 min-w-0">
                    <NativeMediaPlayer url={mediaUrl} type={directMediaType} mediaRef={nativeMediaRef} />
                  </div>
                )}
              </div>

              {/* Bottom row: recommendation badge + summary */}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={cn('inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-semibold', rec.className)}
                  >
                    {rec.label}
                  </span>
                  <span className="text-sm text-muted-foreground">Confidence: {Math.round(analysis.confidence * 100)}%</span>
                </div>
                <p className="text-sm leading-relaxed">{analysis.editorialSummary}</p>
              </div>
            </CardContent>
          </Card>

          {/* Tabbed detail panels */}
          <Tabs defaultValue="segments" className="space-y-4">
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="segments">Top Segments</TabsTrigger>
              <TabsTrigger value="claims">Claims ({analysis.claimsToVerify.length})</TabsTrigger>
              <TabsTrigger value="risks">Risk Flags ({analysis.riskFlags.length})</TabsTrigger>
              <TabsTrigger value="angles">Editorial Angles</TabsTrigger>
              <TabsTrigger value="speakers">Speakers & Moments</TabsTrigger>
              <TabsTrigger value="export">Export</TabsTrigger>
            </TabsList>

            {/* Top Segments */}
            <TabsContent value="segments">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Segments</CardTitle>
                </CardHeader>
                <CardContent>
                  {analysis.topSegments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No notable segments identified.</p>
                  ) : (
                    <div className="space-y-3">
                      {analysis.topSegments.map((seg) => (
                        <ExpandableSegment key={seg.rank} seg={seg} onSeek={hasPlayer ? seekTo : null} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Claims to Verify */}
            <TabsContent value="claims">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <AlertTriangle className="h-4 w-4" />
                    Claims to Verify
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analysis.claimsToVerify.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No specific claims flagged for verification.</p>
                  ) : (
                    <div className="space-y-4">
                      {analysis.claimsToVerify.map((claim, i) => (
                        <div key={i} className="rounded-lg border p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-medium flex-1">{claim.claim}</p>
                            <div className="flex items-center gap-2 shrink-0">
                              {claim.verification && <VerdictBadge verdict={claim.verification.finalVerdict} />}
                              <PriorityBadge priority={claim.priority} />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="tabular-nums flex items-center gap-1.5">
                              at{' '}
                              {hasPlayer ? (
                                <SeekButton seconds={claim.timestamp} onSeek={seekTo} />
                              ) : (
                                formatTimestamp(claim.timestamp)
                              )}
                            </span>
                            {claim.verification && (
                              <span className="tabular-nums">Confidence: {Math.round(claim.verification.confidence * 100)}%</span>
                            )}
                          </div>
                          {claim.verification && (
                            <div className="space-y-2 border-t pt-3">
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                  Evidence
                                </p>
                                <p className="text-sm bg-muted/50 rounded p-2">{claim.verification.evidence}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                  Reasoning
                                </p>
                                <p className="text-sm bg-muted/50 rounded p-2">{claim.verification.reasoning}</p>
                              </div>
                              {claim.verification.adversarialNotes &&
                                claim.verification.adversarialNotes !== 'Verdict confirmed by adversarial review' && (
                                  <div>
                                    <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">
                                      Adversarial Review Notes
                                    </p>
                                    <p className="text-sm bg-orange-50 text-orange-900 rounded p-2 border border-orange-200">
                                      {claim.verification.adversarialNotes}
                                    </p>
                                  </div>
                                )}
                              {claim.verification.adversarialNotes === 'Verdict confirmed by adversarial review' && (
                                <p className="text-xs text-green-700 flex items-center gap-1">
                                  <Check className="h-3 w-3" /> Verdict confirmed by adversarial review
                                </p>
                              )}
                              {claim.verification.sourceReferences.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                    Sources
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {claim.verification.sourceReferences.map((src, j) => (
                                      <span
                                        key={j}
                                        className="text-xs bg-blue-50 text-blue-800 rounded px-2 py-0.5 border border-blue-200"
                                      >
                                        {src}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {!claim.verification && claim.verificationNotes && (
                            <p className="text-sm text-muted-foreground bg-muted/50 rounded p-2">{claim.verificationNotes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Risk Flags */}
            <TabsContent value="risks">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShieldAlert className="h-4 w-4" />
                    Risk Flags
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analysis.riskFlags.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No risk flags identified.</p>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {analysis.riskFlags.map((flag, i) => (
                        <div key={i} className="rounded-lg border p-4 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold">{flag.type}</span>
                            <SeverityBadge severity={flag.severity} />
                          </div>
                          <p className="text-sm text-muted-foreground">{flag.explanation}</p>
                          {flag.relatedTimestamps.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {flag.relatedTimestamps.map((ts, j) =>
                                hasPlayer ? (
                                  <SeekButton key={j} seconds={ts} onSeek={seekTo} className="text-[10px]" />
                                ) : (
                                  <Badge key={j} variant="outline" className="text-[10px] tabular-nums">
                                    {formatTimestamp(ts)}
                                  </Badge>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Editorial Angles */}
            <TabsContent value="angles">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Lightbulb className="h-4 w-4" />
                    Suggested Editorial Angles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analysis.editorialAngles.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No editorial angles suggested.</p>
                  ) : (
                    <ol className="space-y-3">
                      {analysis.editorialAngles.map((angle, i) => (
                        <li key={i} className="flex gap-3">
                          <span
                            className={cn(
                              'flex h-6 w-6 shrink-0 items-center justify-center',
                              'rounded-full bg-primary/10 text-primary text-xs font-bold',
                            )}
                          >
                            {i + 1}
                          </span>
                          <p className="text-sm pt-0.5">{angle}</p>
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Speakers & Key Moments */}
            <TabsContent value="speakers">
              {/* Speaker Profiles */}
              {visualContext && visualContext.speakers.length > 0 && (
                <Card className="mb-4">
                  <CardHeader>
                    <CardTitle className="text-base">Speakers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {visualContext.speakers.map((speaker, i) => {
                        const color = getSpeakerColor(speaker.label, speakerColorMap);
                        return (
                          <div key={i} className={cn('rounded-lg border overflow-hidden', color.border)}>
                            <div className="flex gap-3 p-3">
                              {speaker.imageUrl ? (
                                <img
                                  src={speaker.imageUrl}
                                  alt={speaker.label}
                                  className="w-16 h-16 rounded-lg object-cover shrink-0"
                                />
                              ) : (
                                <div
                                  className={cn(
                                    'w-16 h-16 rounded-lg shrink-0 flex items-center justify-center text-lg font-bold',
                                    color.bg,
                                    color.text,
                                  )}
                                >
                                  {speaker.label
                                    .split(' ')
                                    .map((n) => n[0])
                                    .join('')
                                    .substring(0, 2)}
                                </div>
                              )}
                              <div className="min-w-0 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', color.dot)} />
                                  <span className={cn('font-semibold text-sm', color.text)}>{speaker.label}</span>
                                </div>
                                {speaker.role && <p className="text-xs font-medium text-muted-foreground">{speaker.role}</p>}
                                {speaker.bio && <p className="text-xs text-muted-foreground leading-relaxed">{speaker.bio}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Keyframes */}
              {visualContext && visualContext.keyframes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Key Moments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {visualContext.keyframes.map((kf, i) => (
                        <div key={i} className="rounded-lg border p-3 flex gap-3">
                          <span className="text-xs tabular-nums text-muted-foreground shrink-0 pt-0.5 w-12 text-right">
                            {hasPlayer ? (
                              <SeekButton seconds={kf.timestampSec} onSeek={seekTo} />
                            ) : (
                              formatTimestamp(kf.timestampSec)
                            )}
                          </span>
                          <div className="space-y-1 min-w-0">
                            <p className="text-sm">{kf.description}</p>
                            {kf.speakersVisible.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {kf.speakersVisible.map((s, j) => {
                                  const c = getSpeakerColor(s, speakerColorMap);
                                  return (
                                    <span key={j} className={cn('text-[10px] rounded px-1.5 py-0.5', c.bg, c.text)}>
                                      {s}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {(!visualContext || (visualContext.speakers.length === 0 && visualContext.keyframes.length === 0)) && (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">No speaker or moment data available.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Export */}
            <TabsContent value="export">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Export Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="outline" onClick={handleDownloadJson} className="gap-2">
                      <Download className="h-4 w-4" />
                      Download JSON
                    </Button>
                    <Button variant="outline" onClick={handleCopyToClipboard} className="gap-2">
                      {copied ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy to Clipboard
                        </>
                      )}
                    </Button>
                    {metadata?.sourceUrl && (
                      <Button variant="outline" asChild className="gap-2">
                        <a href={metadata.sourceUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                          Open Source
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ── Right transcript panel ── */}
      {transcriptOpen && hasTranscript && transcript && (
        <div className="w-80 shrink-0 border-l bg-background flex flex-col overflow-hidden">
          <TranscriptPanel
            transcript={transcript}
            speakerColorMap={speakerColorMap}
            highlightedTimestamps={highlightedTimestamps}
            currentTime={currentTime}
            hasPlayer={hasPlayer}
            onSeek={seekTo}
            onClose={() => setTranscriptOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
